import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithCredential,
  onAuthStateChanged,
  type User as FirebaseUser,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { auth, isIOSNative } from './app';

export function onAuthChange(callback: (user: FirebaseUser | null) => void) {
  if (isIOSNative) {
    // iOS: Wait for authStateReady() before registering listener
    auth.authStateReady().then(() => {
      onAuthStateChanged(auth, callback);
    });
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
}

export async function login(email: string, password: string) {
  return signInWithEmailAndPassword(auth, email, password);
}

export async function signup(email: string, password: string) {
  return createUserWithEmailAndPassword(auth, email, password);
}


export async function logout() {
  return signOut(auth);
}

export async function resetPassword(email: string) {
  return sendPasswordResetEmail(auth, email);
}

export async function changePassword(currentPassword: string, newPassword: string) {
  const user = auth.currentUser;
  if (!user || !user.email) throw new Error('No authenticated user');

  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  await updatePassword(user, newPassword);
}

export async function googleLogin() {
  if (Capacitor.isNativePlatform()) {
    // Native: use Capacitor Firebase Authentication plugin
    const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
    const result = await FirebaseAuthentication.signInWithGoogle();
    const idToken = result.credential?.idToken;
    if (!idToken) throw new Error('Google Sign-In failed: no idToken');
    const credential = GoogleAuthProvider.credential(idToken);
    return signInWithCredential(auth, credential);
  }
  // Web: use popup
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return signInWithPopup(auth, provider);
}

export async function appleLogin() {
  if (Capacitor.isNativePlatform()) {
    // Native: use Capacitor Firebase Authentication plugin with per-call skipNativeAuth.
    //
    // By default the plugin performs Firebase sign-in on the native side and then returns.
    // Apple idTokens are SINGLE-USE, so calling signInWithCredential on the JS side after
    // that consumes the token a second time and Firebase rejects with
    // "Duplicate credential received. (auth/missing-or-invalid-nonce)" (seen on iPadOS
    // 26.4). We therefore pass `skipNativeAuth: true`, which makes the plugin only run the
    // Apple authorization UI and return the credential — the JS layer is then the only
    // caller of signInWithCredential, and the token is consumed exactly once.
    const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
    let result;
    try {
      result = await FirebaseAuthentication.signInWithApple({ skipNativeAuth: true });
    } catch (err: any) {
      const code = err?.code ?? err?.errorCode ?? '';
      const message = err?.message ?? String(err);
      if (message.includes('cancel') || code === '1001' || code === 'ERR_CANCELED') {
        throw new Error('cancelled');
      }
      console.error('[AppleSignIn] Native plugin error:', code, message);
      throw new Error(`Apple Sign-In failed: ${message}`);
    }

    const idToken = result.credential?.idToken;
    const rawNonce = result.credential?.nonce;
    if (!idToken) {
      console.error('[AppleSignIn] Missing idToken in credential', result.credential);
      throw new Error('Apple Sign-In failed: no idToken returned from Apple');
    }

    const provider = new OAuthProvider('apple.com');
    const credential = rawNonce
      ? provider.credential({ idToken, rawNonce })
      : provider.credential({ idToken });
    return signInWithCredential(auth, credential);
  }
  // Web: use popup
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');
  return signInWithPopup(auth, provider);
}

export async function sendVerificationEmail(user?: FirebaseUser | null) {
  const target = user ?? auth.currentUser;
  if (!target) throw new Error('No authenticated user');
  return sendEmailVerification(target);
}

export async function reloadUser(user: FirebaseUser) {
  await user.reload();
  return user.emailVerified;
}

export function getCurrentFirebaseUser(): FirebaseUser | null {
  return auth.currentUser;
}

/**
 * Auth 토큰 강제 갱신 + 복구 불가 시 강제 로그아웃.
 * 1차: 토큰 갱신 → 리스너 재시도
 * 2차(갱신 성공했는데 여전히 실패): 세션 복구 불가 → 강제 로그아웃
 */
let refreshing = false;
let refreshAttempted = false;

export async function tryRefreshAuth(): Promise<boolean> {
  if (refreshing) return false;

  // 이미 토큰 갱신을 시도했는데 또 실패 → 복구 불가 → 강제 로그아웃
  if (refreshAttempted) {
    console.warn('[Auth] Token refresh succeeded but Firestore still denied — forcing logout');
    refreshAttempted = false;
    try { await signOut(auth); } catch {}
    return false;
  }

  const user = auth.currentUser;
  if (!user) {
    console.warn('[Auth] No current user — cannot refresh token');
    return false;
  }

  refreshing = true;
  try {
    await user.getIdToken(true);
    console.log('[Auth] Token refreshed successfully');
    refreshing = false;
    refreshAttempted = true; // 다음 실패 시 로그아웃
    return true;
  } catch (err) {
    console.error('[Auth] Token refresh failed — forcing logout:', err);
    refreshing = false;
    try { await signOut(auth); } catch {}
    return false;
  }
}

/** 리스너가 서버에서 데이터를 받으면 호출 — 복구 플래그 리셋 */
export function markAuthHealthy() {
  refreshAttempted = false;
}
